import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

type Profile = { user_id: string; full_name: string | null; email: string | null };

const cache = new Map<string, Profile | null>();
const inflight = new Map<string, Promise<void>>();
const subscribers = new Set<() => void>();

function notify() { subscribers.forEach((fn) => fn()); }

async function fetchProfiles(ids: string[]) {
  const missing = ids.filter((id) => !cache.has(id) && !inflight.has(id));
  if (missing.length === 0) return;
  const promise = (async () => {
    const { data, error } = await supabase.rpc("admin_get_operation_users", {
      p_user_ids: missing,
    });
    if (error) {
      console.error("account display lookup failed", error);
      missing.forEach((id) => cache.set(id, null));
      notify();
      return;
    }
    const got = new Map<string, Profile>();
    (data || []).forEach((p: any) => got.set(p.user_id, p));
    missing.forEach((id) => cache.set(id, got.get(id) ?? null));
    notify();
  })();
  missing.forEach((id) => inflight.set(id, promise));
  await promise;
  missing.forEach((id) => inflight.delete(id));
}

export function useUserProfilesPreload(ids: (string | null | undefined)[]) {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    subscribers.add(fn);
    const clean = Array.from(new Set(ids.filter((x): x is string => !!x)));
    if (clean.length) fetchProfiles(clean);
    return () => { subscribers.delete(fn); };
  }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
}

function initials(name: string | null | undefined, email: string | null | undefined) {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

/** Read a profile already resolved by UserCell / useUserProfilesPreload (for search + CSV export). */
export function getCachedProfile(userId: string | null | undefined): Profile | null {
  if (!userId) return null;
  return cache.get(userId) ?? null;
}

export function userSearchText(userId: string | null | undefined): string {
  const p = getCachedProfile(userId);
  return [p?.full_name, p?.email, userId].filter(Boolean).join(" ");
}

/** Case-insensitive match of a query against an account's name, email or id. */
export function matchesUserSearch(userId: string | null | undefined, query: string): boolean {
  if (!query) return true;
  return userSearchText(userId).toLowerCase().includes(query.toLowerCase());
}

export function userDisplayName(userId: string | null | undefined): string {
  if (!userId) return "—";
  const p = getCachedProfile(userId);
  return p?.full_name || p?.email || `Unknown account ${userId.slice(0, 8)}`;
}

export function userEmail(userId: string | null | undefined): string {
  return getCachedProfile(userId)?.email || "";
}

export default function UserCell({
  userId,
  compact = false,
  showEmail,
  showAvatar = true,
  className,
}: {
  userId: string | null | undefined;
  compact?: boolean;
  showEmail?: boolean;
  showAvatar?: boolean;
  className?: string;
}) {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    subscribers.add(fn);
    if (userId) fetchProfiles([userId]);
    return () => { subscribers.delete(fn); };
  }, [userId]);

  if (!userId) return <span className="text-xs text-muted-foreground">—</span>;
  const p = cache.get(userId);
  const name = p?.full_name || null;
  const email = p?.email || null;
  const withEmail = showEmail ?? !compact;

  return (
    <Link
      to={`/admin/users/${userId}`}
      onClick={(e) => e.stopPropagation()}
      className={`flex items-center gap-2 hover:underline min-w-0 ${className || ""}`}
    >
      {showAvatar && (
        <Avatar className="h-7 w-7 shrink-0">
          <AvatarFallback className="text-[10px]">{initials(name, email)}</AvatarFallback>
        </Avatar>
      )}
      <div className="min-w-0">
        <div className="text-sm font-medium truncate leading-tight">
          {name || email || <span className="text-muted-foreground">Unknown account {userId.slice(0, 8)}</span>}
        </div>
        {withEmail && email && name && (
          <div className="text-xs text-muted-foreground truncate leading-tight">{email}</div>
        )}
      </div>
    </Link>
  );
}
